import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Popover,
    Stack,
    Tooltip,
    Typography
} from '@mui/material';
import TimelineDot from '@mui/lab/TimelineDot';
import CloseIcon from '@mui/icons-material/Close';
import { useDispatch } from 'react-redux';
import JSDB from 'fromKotlin/nk';
import { dateObjectToString, roundDecimals } from 'fromKotlin/nkutils';
import {
    getClientPriceChanges,
    PriceChangeRow,
    updateAllPrice
} from 'api/customer/products_prices.api';
import NoNegative, { WithNegative } from 'ui-component/textFieldNumber';
import { SNACKBAR_OPEN } from 'store/actions';
import { CurrencyShekel, Percentage } from 'tabler-icons-react';

const HISTORY_YEARS = 2;

const resolvePriceClientId = (cid: number): number => {
    const client = JSDB().getClient(cid)?.first;
    if (!client) return cid;
    const master = client.masterBranch;
    if (master) {
        const pc = Number(master.price_control ?? 0);
        if (pc !== 0) return pc;
        const mid = Number(master.id);
        if (mid) return mid;
    }
    const pc = Number(client.price_control ?? 0);
    return pc !== 0 ? pc : cid;
};

const formatDate = (d: string) => {
    if (!d) return '';
    try {
        return JSDB().getDatesManipulator().dateIsrael(d);
    } catch {
        return d;
    }
};

const formatDateTime = (t: string) => {
    if (!t) return '';
    try {
        return JSDB().getDatesManipulator().dateTimeIsrael(t) ?? t;
    } catch {
        return t;
    }
};

const rowKey = (row: PriceChangeRow, index: number) =>
    `${row.date}|${row.action_time}|${row.price}|${row.discount}|${index}`;

export type PriceHistoryTarget = {
    productId: number;
    productName: string;
    master?: number | null;
};

type PriceHistoryDialogProps = {
    open: boolean;
    onClose: () => void;
    clientId: number;
    product: PriceHistoryTarget | null;
    canEdit: boolean;
    onSaved?: () => void;
};

const PriceHistoryDialog = ({
    open,
    onClose,
    clientId,
    product,
    canEdit,
    onSaved
}: PriceHistoryDialogProps) => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rows, setRows] = useState<PriceChangeRow[]>([]);
    const [fromDate, setFromDate] = useState('');
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [active, setActive] = useState<PriceChangeRow | null>(null);
    const [draftPrice, setDraftPrice] = useState(0);
    const [draftDiscount, setDraftDiscount] = useState(0);

    const load = useCallback(async () => {
        if (!open || !product?.productId || !clientId) return;
        const to = new Date();
        const from = new Date();
        from.setFullYear(from.getFullYear() - HISTORY_YEARS);
        const fromStr = dateObjectToString(from);
        const toStr = dateObjectToString(to);
        setFromDate(fromStr);
        setLoading(true);
        try {
            const ids = [Number(clientId)];
            const resolved = resolvePriceClientId(Number(clientId));
            if (resolved && resolved !== Number(clientId)) ids.push(resolved);
            const data = await getClientPriceChanges(
                fromStr,
                toStr,
                ids,
                [Number(product.productId)],
                { keepSnapshot: true }
            );
            data.sort((a, b) => {
                const byDate = String(a.date).localeCompare(String(b.date));
                if (byDate !== 0) return byDate;
                return String(a.action_time).localeCompare(String(b.action_time));
            });
            setRows(data);
        } catch (e) {
            console.error(e);
            setRows([]);
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'שגיאה בטעינת היסטוריית מחיר',
                variant: 'alert',
                alertSeverity: 'error'
            });
        } finally {
            setLoading(false);
        }
    }, [open, product?.productId, clientId, dispatch]);

    useEffect(() => {
        if (!open) {
            setRows([]);
            setActive(null);
            setAnchorEl(null);
            return;
        }
        void load();
    }, [open, load]);

    const afterDiscount = useCallback(
        (price: number, discount: number) => roundDecimals(Number(price) * (1 - Number(discount) / 100), 2),
        []
    );

    const openDot = (event: React.MouseEvent<HTMLElement>, row: PriceChangeRow) => {
        event.stopPropagation();
        setActive(row);
        setDraftPrice(roundDecimals(Number(row.price) || 0, 4));
        setDraftDiscount(roundDecimals(Number(row.discount) || 0, 4));
        setAnchorEl(event.currentTarget);
    };

    const closePopover = () => {
        setAnchorEl(null);
        setActive(null);
    };

    const saveActive = async () => {
        if (!active || !product || !canEdit) return;
        const price = roundDecimals(Math.max(0, Number(draftPrice) || 0), 4);
        const discount = Math.min(100, Math.max(-100, roundDecimals(Number(draftDiscount) || 0, 4)));
        setSaving(true);
        try {
            await updateAllPrice(
                [
                    {
                        id: Number(clientId),
                        product_id: Number(product.productId),
                        price,
                        discount,
                        master: product.master && product.master !== -1 ? product.master : -1
                    }
                ],
                active.date
            );
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: `המחיר עודכן ל־${formatDate(active.date)}`,
                variant: 'alert',
                alertSeverity: 'success'
            });
            closePopover();
            await load();
            onSaved?.();
        } catch (e) {
            console.error(e);
            dispatch({
                type: SNACKBAR_OPEN,
                open: true,
                message: 'אירעה שגיאה בעדכון המחיר',
                variant: 'alert',
                alertSeverity: 'error'
            });
        } finally {
            setSaving(false);
        }
    };

    const titleName = product?.productName || (product ? `#${product.productId}` : '');

    const popoverOpen = Boolean(anchorEl) && Boolean(active);

    const rtlText = {
        direction: 'rtl' as const,
        textAlign: 'right' as const,
        width: '100%',
        display: 'block' as const
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="md"
            scroll="paper"
            PaperProps={{
                dir: 'rtl',
                style: { direction: 'rtl', textAlign: 'right' }
            }}
        >
            <DialogTitle dir="rtl" style={{ direction: 'rtl', textAlign: 'right', paddingBottom: 8 }}>
                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns: 'auto 1fr',
                        width: '100%',
                        alignItems: 'start',
                        direction: 'ltr'
                    }}
                >
                    <IconButton onClick={onClose} size="small">
                        <CloseIcon />
                    </IconButton>
                    <div dir="rtl" style={rtlText}>
                        <Typography variant="h3" align="right" style={rtlText}>
                            {titleName}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" align="right" style={rtlText}>
                            היסטוריית מחיר — {HISTORY_YEARS} השנים האחרונות
                            {canEdit ? ' · לחצו על נקודה כדי לערוך מחיר והנחה' : ''}
                        </Typography>
                    </div>
                </div>
            </DialogTitle>
            <DialogContent dividers dir="rtl" style={{ direction: 'rtl', textAlign: 'right' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                        <CircularProgress />
                    </Box>
                ) : rows.length === 0 ? (
                    <Typography color="textSecondary" align="right" style={{ ...rtlText, padding: '32px 0' }}>
                        לא נמצאו שינויי מחיר בטווח זה
                    </Typography>
                ) : (
                    rows.map((row, index) => {
                        const isSnapshot = Boolean(fromDate) && row.date < fromDate;
                        const isLast = index === rows.length - 1;
                        const selected = active?.date === row.date && active?.action_time === row.action_time;
                        return (
                            <div
                                key={rowKey(row, index)}
                                style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr auto',
                                    width: '100%',
                                    columnGap: 12,
                                    direction: 'ltr'
                                }}
                            >
                                <div
                                    dir="rtl"
                                    style={{ ...rtlText, padding: '10px 0', cursor: 'pointer' }}
                                    onClick={(e) => openDot(e, row)}
                                >
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            justifyContent: 'flex-start',
                                            alignItems: 'center',
                                            gap: 8,
                                            width: '100%',
                                            direction: 'rtl'
                                        }}
                                    >
                                        <Typography variant="subtitle1" align="right" style={{ fontWeight: 700 }}>
                                            {formatDate(row.date)}
                                        </Typography>
                                        {isSnapshot && (
                                            <Chip size="small" label="תחילת תקופה" variant="outlined" />
                                        )}
                                        {Number(row.client_id) !== Number(clientId) && (
                                            <Chip size="small" label="מחירון" variant="outlined" color="info" />
                                        )}
                                    </div>
                                    <Typography variant="body2" align="right" style={{ ...rtlText, marginTop: 2 }}>
                                        ₪ {roundDecimals(row.price, 2)}
                                        {Number(row.discount) !== 0
                                            ? ` · הנחה ${roundDecimals(row.discount, 2)}% · אחרי הנחה ₪ ${afterDiscount(row.price, row.discount)}`
                                            : ''}
                                    </Typography>
                                    <Typography variant="caption" color="textSecondary" align="right" style={rtlText}>
                                        {row.agent ? `סוכן: ${row.agent}` : 'סוכן: לא ידוע'}
                                        {row.action_time ? ` · עודכן ${formatDateTime(row.action_time)}` : ''}
                                    </Typography>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4 }}>
                                    <Tooltip title={canEdit ? 'ערוך מחיר בנקודה זו' : 'פרטי השינוי'}>
                                        <span
                                            onClick={(e) => openDot(e, row)}
                                            style={{ display: 'inline-flex', cursor: 'pointer' }}
                                        >
                                            <TimelineDot
                                                color={selected ? 'primary' : 'grey'}
                                                variant={selected ? 'filled' : 'outlined'}
                                                sx={{
                                                    cursor: 'pointer',
                                                    transition: 'transform 0.15s ease',
                                                    '&:hover': { transform: 'scale(1.25)', borderColor: 'primary.main' }
                                                }}
                                            />
                                        </span>
                                    </Tooltip>
                                    {!isLast && (
                                        <div style={{ width: 2, flex: 1, background: 'rgba(0,0,0,0.12)', margin: '4px 0', minHeight: 12 }} />
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </DialogContent>
            <Popover
                open={popoverOpen}
                anchorEl={anchorEl}
                onClose={closePopover}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                PaperProps={{
                    dir: 'rtl',
                    style: { padding: 16, width: 280, direction: 'rtl', textAlign: 'right' }
                }}
            >
                {active && (
                    <Stack
                        spacing={1.5}
                        dir="rtl"
                        style={{ direction: 'rtl', textAlign: 'right' }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && canEdit && !saving) {
                                e.preventDefault();
                                void saveActive();
                            }
                        }}
                    >
                        <Typography variant="subtitle1" align="right" style={{ fontWeight: 700, ...rtlText }}>
                            {formatDate(active.date)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary" align="right" style={rtlText}>
                            {active.agent ? `סוכן: ${active.agent}` : 'סוכן: לא ידוע'}
                            {active.action_time ? ` · ${formatDateTime(active.action_time)}` : ''}
                        </Typography>
                        <NoNegative
                            label="מחיר"
                            size="small"
                            fullWidth
                            disabled={!canEdit || saving}
                            value={draftPrice}
                            changer={setDraftPrice}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <CurrencyShekel size={16} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <WithNegative
                            label="הנחה"
                            size="small"
                            fullWidth
                            disabled={!canEdit || saving}
                            value={draftDiscount}
                            changer={setDraftDiscount}
                            inputProps={{ style: { textAlign: 'right' } }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Percentage size={16} />
                                    </InputAdornment>
                                )
                            }}
                        />
                        {Number(draftDiscount) !== 0 && (
                            <Typography variant="caption" color="textSecondary" align="right" style={rtlText}>
                                אחרי הנחה ₪ {afterDiscount(draftPrice, draftDiscount)}
                            </Typography>
                        )}
                        {canEdit && (
                            <Button
                                variant="contained"
                                onClick={() => void saveActive()}
                                disabled={saving}
                            >
                                {saving ? 'שומר...' : 'שמור'}
                            </Button>
                        )}
                    </Stack>
                )}
            </Popover>
        </Dialog>
    );
};

export default PriceHistoryDialog;
